import { useState } from "react";
import { BadgeCheck, Save, Stamp } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { payApi, type PolicyField, type PolicyResponse, type Sections } from "../../../api/pay";
import { Button, StatusPill } from "../../../components/ui";
import { ApproveModal, AdvisorModal } from "./PolicyModals";

const inp = "h-10 w-full rounded-sm border border-white/15 bg-surface px-3 text-sm text-white disabled:opacity-60";

function FieldInput({ f, value, onChange, disabled }: { f: PolicyField; value: unknown; onChange: (v: unknown) => void; disabled: boolean }) {
  const id = `policy-${f.section}-${f.key}`;
  if (f.kind === "bool")
    return <input id={id} type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="h-5 w-5 accent-[#ff1d00]" data-testid={id} />;
  if (f.kind === "enum")
    return (
      <select id={id} className={inp} value={String(value)} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={id}>
        {f.options?.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  const num = f.kind === "int";
  return <input id={id} className={inp} type={f.kind === "date" ? "date" : "text"} inputMode={num ? "numeric" : undefined} value={String(value ?? "")} disabled={disabled}
    onChange={(e) => onChange(num ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} data-testid={id} />;
}

export function PolicyTab({ data, onChanged }: { data: PolicyResponse; onChanged: () => void }) {
  const [viewId, setViewId] = useState(data.latest.id);
  const doc = data.versions.find((v) => v.id === viewId) ?? data.latest;
  const [sections, setSections] = useState<Sections>(structuredClone(data.latest.sections));
  const [modal, setModal] = useState<"approve" | "advisor" | null>(null);
  const [busy, setBusy] = useState(false);
  const editable = doc.id === data.latest.id;
  const shown = editable ? sections : doc.sections;
  const inUse = data.versions.find((v) => v.id === data.in_use.id);
  const changed = (s: string, k: string) => inUse && inUse.id !== doc.id && inUse.sections[s]?.[k] !== shown[s]?.[k];
  const save = async () => {
    setBusy(true);
    try {
      const r = await payApi.saveDraft(sections);
      toast.success(r.note ? `Draft v${r.version} saved. ${r.note}` : `Draft v${r.version} saved (UNAPPROVED)`);
      setViewId(r.id);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const groups = [...new Set(data.fields.map((f) => f.section))];
  return (
    <section data-testid="pay-policy-tab" className="grid gap-8 lg:grid-cols-[1fr_280px]">
      <div>
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <h2 className="font-display text-lg font-bold uppercase text-white" data-testid="policy-viewing-version">Version {doc.version}</h2>
          <StatusPill status={doc.status} testId="policy-viewing-status" />
          {doc.advisor_validated && <span className="label-caps text-emerald-300" data-testid="policy-advisor-validated">Advisor validated · {doc.advisor_name}</span>}
          <div className="ml-auto flex gap-2">
            {editable && <Button variant="secondary" onClick={save} loading={busy} data-testid="policy-save-draft-btn"><Save className="h-4 w-4" aria-hidden />{doc.status === "UNAPPROVED" ? "Save draft" : "Edit as new draft"}</Button>}
            {doc.status === "UNAPPROVED" && editable && <Button onClick={() => setModal("approve")} data-testid="policy-approve-btn"><Stamp className="h-4 w-4" aria-hidden />Approve policy (business)</Button>}
            {doc.status === "BUSINESS_APPROVED" && <Button variant="secondary" onClick={() => setModal("advisor")} data-testid="policy-advisor-btn"><BadgeCheck className="h-4 w-4" aria-hidden />Record advisor validation</Button>}
          </div>
        </div>
        {doc.status === "BUSINESS_APPROVED" && editable && <p className="mb-4 text-xs text-zinc-400" data-testid="policy-immutable-note">Approved versions are immutable. Saving creates a new UNAPPROVED draft.</p>}
        {groups.map((g) => (
          <fieldset key={g} className="mb-6 rounded-sm border border-white/10 p-5" data-testid={`policy-section-${g}`}>
            <legend className="label-caps px-2 text-signal">{g.replace("_", " ")}</legend>
            <div className="grid gap-5 sm:grid-cols-2">
              {data.fields.filter((f) => f.section === g).map((f) => (
                <div key={f.key} className={changed(f.section, f.key) ? "rounded-sm bg-signal/10 p-2 ring-1 ring-signal/40" : ""}>
                  <label htmlFor={`policy-${f.section}-${f.key}`} className="mb-1 block text-xs font-semibold text-zinc-200">
                    {f.label}{f.advisor_review && <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-300" data-testid={`policy-advisor-flag-${f.section}-${f.key}`}>Advisor review</span>}
                  </label>
                  <FieldInput f={f} value={shown[f.section]?.[f.key]} disabled={!editable}
                    onChange={(v) => setSections((s) => ({ ...s, [f.section]: { ...s[f.section], [f.key]: v as never } }))} />
                  {f.help && <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">{f.help}</p>}
                </div>
              ))}
            </div>
          </fieldset>
        ))}
        <div className="rounded-sm border border-white/10 p-5" data-testid="policy-minimum-wages">
          <p className="label-caps mb-2 text-signal">Minimum wages (effective-dated)</p>
          {data.minimum_wages.map((m) => <p key={m.id} className="text-sm text-zinc-300">{m.jurisdiction} ${(m.cents / 100).toFixed(2)} from {m.effective_from} <span className="text-amber-300">· {m.note}</span></p>)}
          <p className="mt-2 text-xs text-zinc-500">Daily OT eligibility threshold $18.00 is derived (1.5 × $12.00).</p>
        </div>
      </div>
      <aside data-testid="policy-versions">
        <p className="label-caps mb-3 text-zinc-400">Versions</p>
        <ul className="space-y-2">
          {data.versions.map((v) => (
            <li key={v.id}>
              <button onClick={() => setViewId(v.id)} data-testid={`policy-version-${v.version}`}
                className={`w-full rounded-sm border px-3 py-2 text-left text-xs transition-colors ${v.id === doc.id ? "border-signal bg-signal/10" : "border-white/10 hover:border-white/30"}`}>
                <span className="font-semibold text-white">v{v.version}</span> · {v.status}{v.id === data.in_use.id && " · in use"}
                {v.approval_note && <span className="block text-zinc-500">“{v.approval_note}”</span>}
              </button>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[11px] text-zinc-500">Highlighted fields differ from the version in use (v{data.in_use.version}).</p>
      </aside>
      {modal === "approve" && <ApproveModal id={doc.id} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />}
      {modal === "advisor" && <AdvisorModal id={doc.id} onClose={() => setModal(null)} onDone={() => { setModal(null); onChanged(); }} />}
    </section>
  );
}
